'use client';

/**
 * 카테고리 LP 상품 무한스크롤 + 필터/정렬 URL 동기화 (sale 과 동일 골격, 뷰포트 게이트 없음).
 */
import { CACHE_KEY } from '@constants/cacheKey';

import { requestCategoryLpProduct } from '@apis/display/category';
import { getFilterText } from '@components/common/LpFilterProductList/filter';
import { getLpProductV2Sort } from '@utils/lpProductSort';
import type {
  LpCategoryPath,
  LpProductGroupType,
} from '@apis/renewal/lpProduct';
import {
  getLpProductV2QuickFilterQueryString,
  getLpProductV2RequestFromSearchParams,
} from '@components/common/LpFilterProductListV2/filterV2';
import useFilterProductController from '@hooks/useFilterProductContrroller';
import useGa4Event from '@hooks/useGa4Event';
import { useCategoryLpGa } from '@components/pages/category/categoryGa';
import useHistoryRouter from '@hooks/useLinkTo';
import useModalManager from '@hooks/useModalManager';
import useMolocoLogger from '@hooks/useMolocoLogger';
import useMyHeartItem from '@hooks/useMyHeartItem';
import type { CategoryLpServerData } from '@models/categoryMain';
import type {
  FilterBarItem,
  FilterItem,
} from '@wconcept/wconcept-common/models/renewal/common';
import { keepPreviousData, useIsFetching } from '@tanstack/react-query';
import { useWckInfiniteQuery } from '@hooks/wckQuery/useWckInfiniteQuery';
import type { DecodeAssistData } from '@wconcept/wconcept-common/models/display';
import type { Product } from '@wconcept/wconcept-common/models/display/product';
import type { LpProductListCols } from '@wconcept/wconcept-common/utils/lpProductListCols';
import { usePathname, useSearchParams } from 'next/navigation';
import { nextTick } from 'process';
import { useCallback, useEffect, useMemo } from 'react';

interface CategoryLpProductPage {
  number: number;
  totalElements: number;
  totalPages: number;
  content: Product[];
}

export function useCategoryProductList({
  lpPath,
  shopCds = [],
  initialLp,
  assistData,
  isMobile,
  mobileColumnCount,
  addMyHeartProductItem,
  deleteMyHeartProdcutItem,
}: {
  lpPath: LpCategoryPath;
  shopCds?: readonly string[];
  initialLp?: CategoryLpServerData;
  assistData: DecodeAssistData;
  isMobile: boolean;
  mobileColumnCount: LpProductListCols;
  addMyHeartProductItem: (itemCd: string) => void;
  deleteMyHeartProdcutItem: (itemCd: string) => void;
}) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const filterQuery = searchParams.toString();
  const { linkTo } = useHistoryRouter();
  const { openLoadingModal, closeLoadingModal } = useModalManager();
  const {
    sendGa4FilterClick,
    sendGa4SelectItem,
    sendGa4AddToWishProduct,
    getSortText,
  } = useGa4Event();
  const lpGa = useCategoryLpGa(shopCds);
  const { sendMolocoAddToWish } = useMolocoLogger();
  const sort = getLpProductV2Sort(
    searchParams,
    initialLp?.sort || initialLp?.sortInfo?.[0]?.defaultValue
  );
  const saleTags = searchParams.get('saleTags') || '';
  const page = searchParams.get('page') || '1';
  const isFetching = useIsFetching();
  const {
    handlePageChange,
    handleSortChange,
    handleProductClick,
    handleResetFilter,
  } = useFilterProductController();

  const sortName = getSortText(sort, initialLp?.sortInfo?.[0]?.sort);

  const { onClickHeart } = useMyHeartItem({
    isLogin: !!assistData?.isLogin,
    doAfterAddMyHeart: (param, product, index, type) => {
      sendGa4AddToWishProduct({
        product: product as Product,
        listNo: (index || 0) + 1,
        myheartYn: true,
        page: lpGa.page,
        area: lpGa.heartArea(type),
        ecommercePath: lpGa.ecommercePath,
        affiliation: type,
        sortType: sortName,
      });
      sendMolocoAddToWish({
        itemCd: param.itemCd,
        amount: product.finalPrice,
        pageId: pathname,
      });
      addMyHeartProductItem(param.itemCd);
    },
    doAfterDeleteMyHeart: (param, product, index, type) => {
      sendGa4AddToWishProduct({
        product: product as Product,
        listNo: (index || 0) + 1,
        myheartYn: false,
        page: lpGa.page,
        area: lpGa.heartArea(type),
        ecommercePath: lpGa.ecommercePath,
        affiliation: type,
        sortType: sortName,
      });
      deleteMyHeartProdcutItem(param.itemCd);
    },
  });

  useEffect(() => {
    if (isFetching === 0) {
      closeLoadingModal();
    }
  }, [closeLoadingModal, isFetching]);

  useEffect(() => {
    closeLoadingModal();
  }, [closeLoadingModal, initialLp]);

  // MO=무한스크롤(pageParam), PC=페이지네이션(?page=)
  const fetchProduct = useCallback(
    (pageParam: number) =>
      requestCategoryLpProduct<CategoryLpProductPage>(lpPath, {
        ...getLpProductV2RequestFromSearchParams(
          new URLSearchParams(filterQuery)
        ),
        ...(shopCds.length > 0 ? { shopCds } : {}),
        domainType: isMobile ? 'm' : 'pc',
        ...(initialLp?.genderType
          ? { gender: initialLp.genderType.toLowerCase() }
          : {}),
        sort,
        pageNo: !isMobile ? parseInt(page, 10) || 1 : pageParam,
        pageSize: 60,
        saleTags: saleTags ? saleTags.split(',').filter(Boolean) : [],
      } as Parameters<typeof requestCategoryLpProduct>[1]),
    [
      lpPath,
      shopCds,
      filterQuery,
      isMobile,
      initialLp?.genderType,
      page,
      saleTags,
      sort,
    ]
  );

  const { data, fetchNextPage, hasNextPage, isPending } = useWckInfiniteQuery({
    queryKey: [
      CACHE_KEY.CATEGORY.CATEGORY_PRODUCTS,
      lpPath.mediumCd,
      lpPath.largeCategory,
      lpPath.middleCategory ?? '',
      lpPath.smallCategory ?? '',
      isMobile ? 'mobile' : 'pc',
      page,
      filterQuery,
      mobileColumnCount,
    ],
    queryFn: ({ pageParam = 1 }) => fetchProduct(pageParam),
    initialPageParam: parseInt(page, 10) || 1,
    getNextPageParam: lastPage => {
      const pageNumber = lastPage?.number ?? 1;
      const nextPage = pageNumber + 1;
      const lastPageNumber = lastPage?.totalPages ?? 1;
      return nextPage > lastPageNumber ? undefined : nextPage;
    },
    placeholderData: keepPreviousData,
  });

  // 필터/정렬 변경 시 LP 상단으로 스크롤
  const scrollToListTop = useCallback(() => {
    const header =
      document.querySelector('#header') ||
      document.querySelector('#headerDesktop .inner-nav');
    const headerHeight = header?.clientHeight || 0;
    const gnbMenu = document.querySelector('#main-gnb-bar');
    const gnbMenuHeight = gnbMenu?.clientHeight || 0;
    const productList = document.querySelector('.bottom-product-wrap');
    const productListTop = productList?.getBoundingClientRect().top || 0;
    const scrollPosition =
      productListTop + window.pageYOffset - headerHeight - gnbMenuHeight;

    if (productListTop < 0) {
      window.scrollTo(0, scrollPosition);
    }
  }, []);

  // 퀵 필터 토글
  const onChangePreOrderOrFreeDelivery = useCallback(
    (checked: boolean, code: string, groupType: LpProductGroupType) => {
      const queryString = getLpProductV2QuickFilterQueryString(
        searchParams,
        checked,
        code,
        groupType
      );
      const filterText = getFilterText(code);
      openLoadingModal();
      sendGa4FilterClick({
        text: checked ? filterText : `${filterText}해제`,
        page: lpGa.page,
        area: `${lpGa.page}_filter_option`,
      });
      linkTo.replace({
        url: `?${queryString}`,
        option: { scroll: false },
      });
      scrollToListTop();
    },
    [
      lpGa.page,
      linkTo,
      openLoadingModal,
      scrollToListTop,
      searchParams,
      sendGa4FilterClick,
    ]
  );

  // 품절제외 (productType/excludeOutOfStock)
  const onSoldoutChange = useCallback(
    (checked: boolean) => {
      const queryString = getLpProductV2QuickFilterQueryString(
        searchParams,
        checked,
        'excludeOutOfStock',
        'productType'
      );
      openLoadingModal();
      sendGa4FilterClick({
        text: checked ? '품절제외' : '품절제외해제',
        page: lpGa.page,
        area: `${lpGa.page}_filter_soldout`,
      });
      linkTo.replace({
        url: `?${queryString}`,
        option: { scroll: false },
      });
      scrollToListTop();
    },
    [
      lpGa.page,
      linkTo,
      openLoadingModal,
      scrollToListTop,
      searchParams,
      sendGa4FilterClick,
    ]
  );

  const onSortChange = useCallback(
    (sortParam: { code: string; name: string }) => {
      openLoadingModal();
      sendGa4FilterClick({
        text: sortParam.name,
        page: lpGa.page,
        area: `${lpGa.page}_filter_sort`,
      });
      handleSortChange(sortParam);
      setTimeout(() => {
        if (sort === sortParam.code) closeLoadingModal();
      }, 400);
    },
    [
      lpGa.page,
      closeLoadingModal,
      handleSortChange,
      openLoadingModal,
      sendGa4FilterClick,
      sort,
    ]
  );

  const onPageChange = useCallback(
    (value: number) => {
      openLoadingModal();
      handlePageChange(value);
    },
    [handlePageChange, openLoadingModal]
  );

  const onClickProduct = useCallback(
    (product: Product, index: number) => {
      sendGa4SelectItem({
        product,
        listNo: index + 1,
        page: lpGa.page,
        area: lpGa.itemArea,
        ecommercePath: lpGa.ecommercePath,
        sortType: sortName,
        playYn: product.shortFormInfo ? 'Y' : 'N',
      });
      handleProductClick(product);
    },
    [handleProductClick, lpGa, sendGa4SelectItem, sortName]
  );

  const onClickMolocoAdLpProduct = useCallback(
    (product: Product, index: number) => {
      sendGa4SelectItem({
        product,
        listNo: index + 1,
        page: lpGa.page,
        area: lpGa.adItemArea,
        ecommercePath: lpGa.ecommercePath,
        sortType: sortName,
        affiliation: 'moloco',
      });
    },
    [lpGa, sendGa4SelectItem, sortName]
  );

  const onFilterSubmit = useCallback(
    (queryParam: string, selectedFilterList?: FilterItem[], type?: string) => {
      if (type === 'submit') {
        sendGa4FilterClick({
          page: lpGa.page,
          area: `${lpGa.page}_filter_confirm`,
          selectedFilters: selectedFilterList,
        });
      }
      openLoadingModal();
      linkTo.replace({
        url: `?${queryParam}`,
        option: { scroll: false },
      });
      nextTick(() => {
        if (searchParams.toString() === queryParam) closeLoadingModal();
      });
      scrollToListTop();
    },
    [
      lpGa.page,
      closeLoadingModal,
      linkTo,
      openLoadingModal,
      scrollToListTop,
      searchParams,
      sendGa4FilterClick,
    ]
  );

  const handleResetClick = useCallback(
    (text: string) => {
      sendGa4FilterClick({
        text,
        page: lpGa.page,
        area: `${lpGa.page}_filter_reset`,
        selectedFilters: initialLp?.selectedFilterList,
      });
    },
    [lpGa.page, initialLp?.selectedFilterList, sendGa4FilterClick]
  );

  const onResetFilter = useCallback(() => {
    openLoadingModal();
    handleResetClick('초기화');
    handleResetFilter();
  }, [handleResetClick, handleResetFilter, openLoadingModal]);

  const onClickPromoBtn = useCallback(
    (item: FilterBarItem) => {
      sendGa4FilterClick({
        text: item.text,
        page: lpGa.page,
        area: `${lpGa.page}_filter_promotion`,
      });
    },
    [lpGa.page, sendGa4FilterClick]
  );

  const onFilterClose = useCallback(() => {
    sendGa4FilterClick({ page: lpGa.page, area: `${lpGa.page}_filter_cancel` });
  }, [lpGa.page, sendGa4FilterClick]);

  const products = useMemo<Product[]>(
    () =>
      data?.pages?.reduce(
        (items: Product[], current) => [...items, ...(current?.content || [])],
        []
      ) || [],
    [data]
  );

  return {
    sort,
    products,
    totalElements: data?.pages?.[0]?.totalElements || 0,
    totalPages: data?.pages?.[0]?.totalPages || 1,
    page: parseInt(page, 10) || 1,
    hasNextPage,
    isPending,
    fetchNextPage,
    onPageChange,
    onSortChange,
    onChangePreOrderOrFreeDelivery,
    onSoldoutChange,
    onClickProduct,
    onClickHeart,
    onFilterSubmit,
    onResetFilter,
    handleResetClick,
    onClickPromoBtn,
    onFilterClose,
    onClickMolocoAdLpProduct,
  };
}
